/**
 * Admin page for post-deploy setup.
 * Reads contract addresses from .env (MOTOSETTLE_CONFIG).
 */
import React, { useEffect, useState } from "react";
import { useWalletConnect } from "@btc-vision/walletconnect";
import { ABIDataTypes } from "@btc-vision/transaction";
import { getContract, BitcoinAbiTypes } from "opnet";
import { MOTOSETTLE_CONFIG } from "../config";

const ARB_REGISTRY_ABI = [
  {
    name: "setEscrowCore",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "addr", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "success", type: ABIDataTypes.BOOL }],
  },
  {
    name: "register",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "feeRateBps", type: ABIDataTypes.UINT64 }],
    outputs: [{ name: "success", type: ABIDataTypes.BOOL }],
  },
];

const FEE_POLICY_ABI = [
  {
    name: "setEscrowCore",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "addr", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "success", type: ABIDataTypes.BOOL }],
  },
];

const USER_REPUTATION_ABI = [
  {
    name: "addCaller",
    type: BitcoinAbiTypes.Function,
    inputs: [{ name: "caller", type: ABIDataTypes.ADDRESS }],
    outputs: [{ name: "success", type: ABIDataTypes.BOOL }],
  },
  {
    name: "owner",
    type: BitcoinAbiTypes.Function,
    constant: true,
    inputs: [],
    outputs: [{ name: "owner", type: ABIDataTypes.ADDRESS }],
  },
];

export function AdminSetupPage() {
  const { provider, address, walletAddress, network } = useWalletConnect();
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [ownerAddr, setOwnerAddr] = useState<string | null>(null);
  const [ownerCheckLoading, setOwnerCheckLoading] = useState(true);

  const escrowCoreAddr = MOTOSETTLE_CONFIG.escrowCore;
  const arbRegistryAddr = MOTOSETTLE_CONFIG.arbitratorRegistry;
  const feePolicyAddr = MOTOSETTLE_CONFIG.feePolicy;
  const userReputationAddr = MOTOSETTLE_CONFIG.userReputation;

  // Query the on-chain owner() of UserReputation to gate access to this page.
  useEffect(() => {
    if (!provider || !network) return;
    let cancelled = false;
    (async () => {
      setOwnerCheckLoading(true);
      try {
        const c: any = getContract(
          userReputationAddr,
          USER_REPUTATION_ABI as any,
          provider,
          network,
          address ?? undefined,
        );
        const res = await c.owner();
        if (cancelled) return;
        if (res?.revert) {
          setOwnerAddr(null);
        } else {
          const o = res?.properties?.owner?.toString?.() ?? res?.properties?.owner ?? null;
          setOwnerAddr(o ? String(o).toLowerCase() : null);
        }
      } catch {
        if (!cancelled) setOwnerAddr(null);
      } finally {
        if (!cancelled) setOwnerCheckLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [provider, network, userReputationAddr, address]);

  const walletHex = (address as any)?.toString?.()?.toLowerCase() ?? "";
  const isOwner = !!ownerAddr && !!walletHex && ownerAddr === walletHex;

  if (!walletAddress) {
    return (
      <div className="page">
        <div className="page-header"><h1>Admin Setup</h1></div>
        <div className="alert alert-info" style={{ maxWidth: 600 }}>
          Conecta tu wallet para continuar.
        </div>
      </div>
    );
  }

  if (ownerCheckLoading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Admin Setup</h1></div>
        <div className="alert alert-info" style={{ maxWidth: 600 }}>
          Verificando permisos on-chain...
        </div>
      </div>
    );
  }

  if (!isOwner) {
    return (
      <div className="page">
        <div className="page-header"><h1>Admin Setup</h1></div>
        <div className="alert alert-error" style={{ maxWidth: 600 }}>
          <strong>403 — No autorizado.</strong> Esta pagina solo puede ser usada por el owner del protocolo.
          <div style={{ marginTop: 12, fontSize: 12, fontFamily: "monospace", color: "var(--text-muted)" }}>
            Owner on-chain: {ownerAddr ?? "(no pudo consultarse)"}<br />
            Tu wallet:     {walletHex || "(sin address hex)"}
          </div>
        </div>
      </div>
    );
  }

  const callAddCaller = async (contractAddr: string, abi: any[], label: string) => {
    if (!provider || !network || !walletAddress) {
      setStatus("Wallet not connected");
      return;
    }
    setLoading(true);
    setStatus(`Resolving EscrowCore address...`);
    try {
      const escrowCoreResolved = await provider.getPublicKeyInfo(escrowCoreAddr, true);
      if (!escrowCoreResolved) {
        setStatus("Could not resolve EscrowCore address on network");
        setLoading(false);
        return;
      }
      setStatus(`Calling ${label}.addCaller(escrowCore)...`);
      const contract: any = getContract(
        contractAddr,
        abi as any,
        provider,
        network,
        address ?? undefined,
      );
      const result = await contract.addCaller(escrowCoreResolved);
      if ("error" in result) { setStatus(`Error: ${result.error}`); setLoading(false); return; }
      if (result.revert) { setStatus(`Revert: ${result.revert}`); setLoading(false); return; }
      setStatus(`Simulation OK. Sending transaction...`);
      const receipt = await result.sendTransaction({
        signer: null, mldsaSigner: null, refundTo: walletAddress,
        maximumAllowedSatToSpend: 100_000n, feeRate: 10, network,
      });
      setStatus(`${label}.addCaller() sent! Tx: ${receipt.transactionId}`);
    } catch (e: any) {
      setStatus(`Error: ${e.message}`);
    }
    setLoading(false);
  };

  const callSetEscrowCore = async (contractAddr: string, abi: any[], label: string) => {
    if (!provider || !network || !walletAddress) {
      setStatus("Wallet not connected");
      return;
    }

    setLoading(true);
    setStatus(`Resolving EscrowCore address...`);

    try {
      const escrowCoreResolved = await provider.getPublicKeyInfo(escrowCoreAddr, true);
      if (!escrowCoreResolved) {
        setStatus("Could not resolve EscrowCore address on network");
        setLoading(false);
        return;
      }

      setStatus(`Calling ${label}.setEscrowCore()...`);

      const contract = getContract(
        contractAddr,
        abi,
        provider,
        network,
        address ?? undefined,
      );

      const result = await contract.setEscrowCore(escrowCoreResolved);

      if ("error" in result) {
        setStatus(`Error: ${result.error}`);
        setLoading(false);
        return;
      }

      if (result.revert) {
        setStatus(`Revert: ${result.revert}`);
        setLoading(false);
        return;
      }

      setStatus(`Simulation OK. Sending transaction...`);

      const receipt = await result.sendTransaction({
        signer: null,
        mldsaSigner: null,
        refundTo: walletAddress,
        maximumAllowedSatToSpend: 100_000n,
        feeRate: 10,
        network,
      });

      setStatus(`${label}.setEscrowCore() sent! Tx: ${receipt.transactionId}`);
    } catch (e: any) {
      setStatus(`Error: ${e.message}`);
    }

    setLoading(false);
  };

  const registerAsArbitrator = async () => {
    if (!provider || !network || !walletAddress) return;
    setLoading(true);
    setStatus("Registering as arbitrator...");
    try {
      const contract = getContract(
        arbRegistryAddr,
        ARB_REGISTRY_ABI,
        provider,
        network,
        address ?? undefined,
      );
      const result = await contract.register(BigInt(200)); // 200 bps = 2%
      if ("error" in result) { setStatus(`Error: ${result.error}`); setLoading(false); return; }
      if (result.revert) { setStatus(`Revert: ${result.revert}`); setLoading(false); return; }
      setStatus("Simulation OK. Sending transaction...");
      const receipt = await result.sendTransaction({
        signer: null, mldsaSigner: null, refundTo: walletAddress,
        maximumAllowedSatToSpend: 100_000n, feeRate: 10, network,
      });
      setStatus(`Registered as arbitrator! Tx: ${receipt.transactionId}`);
    } catch (e: any) {
      setStatus(`Error: ${e.message}`);
    }
    setLoading(false);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Admin Setup</h1>
        <p>Post-deploy configuration. Only the contract owner (deployer) can execute these.</p>
      </div>

      <div className="card" style={{ maxWidth: 650 }}>
        {/* Current addresses */}
        <div style={{ marginBottom: 20, padding: 12, background: "rgba(255,255,255,0.03)", borderRadius: 6, fontSize: 12, fontFamily: "monospace" }}>
          <div><strong>EscrowCore:</strong> {escrowCoreAddr}</div>
          <div><strong>ArbRegistry:</strong> {arbRegistryAddr}</div>
          <div><strong>FeePolicy:</strong> {feePolicyAddr}</div>
        </div>

        <h3>1. ArbitratorRegistry → setEscrowCore</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Authorizes EscrowCore to call recordCase() on ArbitratorRegistry.
        </p>
        <button
          className="btn btn-primary"
          disabled={loading || !walletAddress}
          onClick={() => callSetEscrowCore(arbRegistryAddr, ARB_REGISTRY_ABI, "ArbitratorRegistry")}
        >
          {loading ? "Sending..." : "Set EscrowCore on ArbitratorRegistry"}
        </button>

        <hr style={{ margin: "24px 0", borderColor: "rgba(255,255,255,0.1)" }} />

        <h3>2. FeePolicy → setEscrowCore</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Authorizes EscrowCore to call collectFee() on FeePolicy.
        </p>
        <button
          className="btn btn-primary"
          disabled={loading || !walletAddress}
          onClick={() => callSetEscrowCore(feePolicyAddr, FEE_POLICY_ABI, "FeePolicy")}
        >
          {loading ? "Sending..." : "Set EscrowCore on FeePolicy"}
        </button>

        <hr style={{ margin: "24px 0", borderColor: "rgba(255,255,255,0.1)" }} />

        <h3>3. UserReputation → addCaller(EscrowCore)</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Authorizes EscrowCore to call recordTrade() / recordDispute() on UserReputation.
          Required for releaseFunds / resolveDispute to succeed.
        </p>
        <button
          className="btn btn-primary"
          disabled={loading || !walletAddress}
          onClick={() => callAddCaller(userReputationAddr, USER_REPUTATION_ABI, "UserReputation")}
        >
          {loading ? "Sending..." : "Add EscrowCore caller on UserReputation"}
        </button>

        <hr style={{ margin: "24px 0", borderColor: "rgba(255,255,255,0.1)" }} />

        <h3>4. Register as Arbitrator</h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Registers the connected wallet as an arbitrator with a fee rate of 200 bps (2%).
        </p>
        <button
          className="btn btn-primary"
          disabled={loading || !walletAddress}
          onClick={registerAsArbitrator}
        >
          {loading ? "Sending..." : "Register as Arbitrator (200 bps)"}
        </button>

        {status && (
          <div
            className={`alert ${status.includes("Tx:") ? "alert-success" : status.startsWith("Error") || status.startsWith("Revert") ? "alert-error" : "alert-info"}`}
            style={{ marginTop: 16 }}
          >
            <span>{status}</span>
          </div>
        )}

        <div style={{ marginTop: 24, fontSize: 12, color: "var(--text-muted)" }}>
          <strong>Connected:</strong> {walletAddress || "Not connected"}<br />
          <strong>Network:</strong> {network?.chainType || "--"}
        </div>
      </div>
    </div>
  );
}
